// Import LitElement base class and html helper function
import {LitElement, html, css, svg} from 'lit';
import {classMap} from 'lit/directives/class-map.js';

export class StreamBoxRowing extends LitElement {
    /**
     * Define properties. Properties defined here will be automatically
     * observed.
     */
    static get properties() {
        return {
            user_name: {type: String},
            max_hr: {type: Number},
            hr: {type: Number},
            percentage_hr: {type: Number},
            power: {type: Number},
            percentage_power: {type: Number},
            cadence: {type: Number},
            ftp: {type: Number},
            box_color: {type: Object},
            hr_zones_range: {type: Array},
            power_zones_range: {type: Array},
            zone_index: {type: Number},
            pace: {type: Number},
            pace_label: {type: String}

        };
    }

    static get styles() {
        return css`
            .box-0 {
                background: var(--box-0-default);
                background: var(--box-0-grad);
            }
            .box-1 {
                background: var(--box-1-default);
                background: var(--box-1-grad);
            }
            .box-2 {
                background: var(--box-2-default);
                background: var(--box-2-grad);
            }
            .box-3 {
                background: var(--box-3-default);
                background: var(--box-3-grad);
            }
            .box-4 {
                background: var(--box-4-default);
                background: var(--box-4-grad);
            }
            .box-power-0 {
                background: var(--box-power-0-default);
                background: var(--box-power-0-grad);
            }
            .box-power-1 {
                background: var(--box-power-1-default);
                background: var(--box-power-1-grad);
            }
            .box-power-2 {
                background: var(--box-power-2-default);
                background: var(--box-power-2-grad);
            }
            .box-power-3 {
                background: var(--box-power-3-default);
                background: var(--box-power-3-grad);
            }
            .box-power-4 {
                background: var(--box-power-4-default);
                background: var(--box-power-4-grad);
            }
            .box-power-5 {
                background: var(--box-power-5-default);
                background: var(--box-power-5-grad);
            }
            .box-power-6 {
                background: var(--box-power-6-default);
                background: var(--box-power-6-grad);
            }
            .stream-box {
                width: var(--stream-box-width);
                height: var(--stream-box-height);
                border-radius: 10px;
                padding: 10px;
                display: flex;
                display: -webkit-flex;
                flex-direction: column;
                flex-wrap: nowrap;
                justify-content: space-between;
                align-items: stretch;
            }
            .stream-box-upper {
                flex: 1 1 auto;
                font-size: 1.6em;
                font-weight: 600;
                text-align: center;
                color: #222222;
                position: relative;
            }
            .stream-box-upper .stream-box-upper-float {
                position: absolute;
                right: 0;
                top: 1em;
            }
            .stream-box-upper .stream-box-upper-float-left {
                position: absolute;
                left: 0;
                top: 1em;
            }
            .stream-box-main {
                flex: 2 1 auto;
                font-size: 2.5em;
                font-weight: 600;
                color: #222222;
                text-align: center;
                display: flex;
                display: -webkit-flex;
                flex-direction: column;
                justify-content: center;
            }
            .stream-box-main .unit {
                font-size: 0.2em;
            }
            .stream-box-main .value {
                font-size: var(--stream-box-main-size);
                margin-bottom: var(--stream-box-main-margin);
                margin-top: var(--stream-box-main-margin);
            }
            .stream-box-footer {
                flex: 1 1 auto;
                -webkit-flex: 1 1 auto;
                font-size: 1.6em;
                font-weight: 600;
            }
            .stream-box-footer .align-left {
                text-align: left;
                float: left;
            }
            .stream-box-footer .align-right {
                text-align: right;
                float: right;
            }
            .icon {
                display: inline-block;
                stroke-width: 0;
                stroke: currentColor;
                fill: currentColor;
                width: 0.9em;
            }
            .icon.w-normal {
                width: 1em;
            }
        `;
    }

    // Custom setter for hr
    set hr(val) {
        let oldVal = this._hr;
        this._hr = val;
        let oldPercentage = this.percentage_hr;
        if (this.max_hr > 0) {
            this.percentage_hr = Math.round((val * 100) / this.max_hr);
        }

        let z_index = 0;
        for(var i=1; i < this.zones_range.length; i++){
            if (this.percentage_hr <= this.zones_range[i]){
                break;
            }
            z_index = z_index + 1;
        }
        let oldBoxColor= this.box_color;
        let zone_key = 'box-' + z_index;
        this.box_color = {'stream-box':true, [zone_key]: true};
        let oldZoneIndex = this.zone_index;
        this.zone_index = z_index + 1;

        this.requestUpdate('box_color', oldBoxColor);
        this.requestUpdate('zone_index', oldZoneIndex);
        this.requestUpdate('hr', oldVal);
        this.requestUpdate('percentage_hr', oldPercentage);
    }

    get hr() { return this._hr; }

    set power(val) {
        let oldVal = this._power;
        this._power = val;
        if (val !== null && val !== 'undefined' && val !== 0 && val !== '') {
            let oldPercentage = this.percentage_power;
            if (this.ftp > 0){
                this.percentage_power = Math.round((val * 100) / this.ftp);
            }
            this.requestUpdate('percentage_power', oldPercentage);
        }
        this.requestUpdate('power', oldVal);
    }

    get power() { return this._power; }

    set pace(val) {
        let oldVal = this._pace;
        this._pace = val;
        let oldPaceLabel = this.pace_label;
        if (val !== null && val !== 'undefined' && val > 0 ) {
            this.pace_label = this.distance_of_time(val);
        } else {
            this.pace_label = '0:00';
        }
        this.requestUpdate('pace', oldVal);
        this.requestUpdate('pace_label', oldPaceLabel);
    }

    get pace() { return this._pace; }

    constructor() {
        super();
        this.max_hr = 0;
        this.box_color = {'stream-box': true, 'box-0': true};
        this.zones_range = [0,60,70,80,90];
        this.user_name = 'Loading';
        this.percentage_hr = 0;
        this.hr = 0;
        this.cadence = 0;
        this.power = 0;
        this.ftp = 0;
        this.percentage_power = 0;
        this.power_zones_range = [0,55,75,90,105,120,150];
        this.zone_index = null;
        this.pace = 0;
        this.pace_label = '0:00';
    }

    render() {
        return html`
            <div class="${classMap(this.box_color)}">
                <div class="stream-box-upper">${this.user_name}
                    ${this.zone_index?
                        html`<span class="stream-box-upper-float-left">Z${this.zone_index}</span>`:
                        html``}
                </div>
                <div class="stream-box-main">
                    <div class="value">${this.pace_label}<span class="unit">/500m</span></div>
                </div>
                <div class="stream-box-footer">
                    <div class="align-left">${this.svgHrIcon} ${this.hr}</div>
                    <div class="align-right">${this.cadence} ${this.svgCadenceIcon}</div>
                </div>
            </div>
        `;
    }

    get svgHrIcon() {
        return svg`<svg viewBox="0 0 100 100" class="icon"><use xlink:href="/img/stream-icons.svg#icon-heart" href="/img/stream-icons.svg#icon-heart"/></svg>`;
    }

    get svgCadenceIcon() {
        return svg`<svg viewBox="0 0 187 155" class="icon w-normal"><use xlink:href="/img/stream-icons.svg#icon-rowing" href="/img/stream-icons.svg#icon-rowing"/></svg>`;
    }

    distance_of_time(val) {
        let output = '';
        let time_in_seconds = val;
        if (time_in_seconds >= 60) {
            let minutes = Math.floor(val / 60)
            time_in_seconds = Math.round((time_in_seconds - (60 * minutes)));
            output += minutes + ':'
        } else {
            output += '0:'
        }
        if (time_in_seconds < 10) {
            output += '0' + time_in_seconds;
        } else {
            output += '' + time_in_seconds;
        }
        return output;
    }

}
